/** @file orbitCamera: Babylon's ortho camera pinned to render-core's view (yaw, clamped pitch, target). */
import { Camera as BabylonCamera } from '@babylonjs/core/Cameras/camera.js';
import { TargetCamera } from '@babylonjs/core/Cameras/targetCamera.js';
import type { Scene } from '@babylonjs/core/scene.js';
import type { Profile } from '@zilo/pose3d-format/types/profile';
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import { viewOf, type View } from '@zilo/pose3d-render-core/camera/viewOf';
import type { Camera } from '@zilo/pose3d-render-core/contract';
import { clamp } from '@zilo/pose3d-std/math/clamp';
import { wrapDegrees } from '@zilo/pose3d-std/math/wrapDegrees';
import { DEPTH_RANGE } from '@zilo/pose3d-runtime-babylon/camera/depthRange';
import { displayOf } from '@zilo/pose3d-runtime-babylon/camera/displayOf';
import { toBabylonVec3 } from '@zilo/pose3d-runtime-babylon/camera/toBabylonVec3';

/**
 * The runtime camera: free yaw (wrapped), pitch clamped to `profile.camera.pitchRange`, a viewport in target pixels
 * and a look-at point. `contract()` is the render-core `Camera`; `view()` its `viewOf` (rounded target, R3); the
 * Babylon camera always projects exactly as render-core's `screenOf` does (pinned by test).
 */
export type OrbitCamera = {
  readonly camera: TargetCamera;
  contract(): Camera;
  view(): View;
  setYaw(deg: number): void;
  orbit(deltaDeg: number): void;
  setPitch(deg: number): void;
  tilt(deltaDeg: number): void;
  resetToProfile(): void;
  setViewport(width: number, height: number): void;
  lookAt(target: Vec3): void;
};

/** Initial yaw, pitch (clamped) and viewport; defaults yaw 0, the profile pitch and the engine's render size. */
export type OrbitInit = { yawDeg?: number; pitchDeg?: number; viewport?: [number, number] };

/** An orthographic `TargetCamera` (no inputs attached) in `scene`, kept in step with the contract camera. */
export function orbitCamera(scene: Scene, profile: Profile, init: OrbitInit = {}): OrbitCamera {
  const [lo, hi] = profile.camera.pitchRange;
  const engine = scene.getEngine();
  const s = {
    yaw: wrapDegrees(init.yawDeg ?? 0), pitch: clamp(init.pitchDeg ?? profile.camera.pitch, lo, hi),
    viewport: init.viewport ?? [engine.getRenderWidth(), engine.getRenderHeight()], target: [0, 0, 0] as Vec3,
  };
  const camera = new TargetCamera('pose3d-camera', toBabylonVec3([0, -DEPTH_RANGE.distance, 0]), scene);
  camera.mode = BabylonCamera.ORTHOGRAPHIC_CAMERA;
  camera.minZ = DEPTH_RANGE.near;
  camera.maxZ = DEPTH_RANGE.far;
  const contract = (): Camera => ({
    kind: 'ortho', yawDeg: s.yaw, pitchDeg: s.pitch, pixelsPerUnit: profile.pixelsPerUnit,
    viewport: [s.viewport[0], s.viewport[1]], target: [s.target[0], s.target[1], s.target[2]],
  });
  const view = (): View => viewOf(contract(), profile);
  const sync = (): void => {
    const v = view();
    const centre = displayOf(v.target, v.zScaleView);
    const back = v.basis.back;
    const d = DEPTH_RANGE.distance;
    camera.position = toBabylonVec3([centre[0] + back[0] * d, centre[1] + back[1] * d, centre[2] + back[2] * d], camera.position);
    camera.setTarget(toBabylonVec3(centre));
    const ppu = v.pixelsPerUnit;
    camera.orthoLeft = -v.originX / ppu;
    camera.orthoRight = (s.viewport[0] - v.originX) / ppu;
    camera.orthoTop = v.originY / ppu;
    camera.orthoBottom = -(s.viewport[1] - v.originY) / ppu;
  };
  const setPitch = (deg: number): void => { s.pitch = clamp(deg, lo, hi); sync(); };
  const setYaw = (deg: number): void => { s.yaw = wrapDegrees(deg); sync(); };
  sync();
  return {
    camera, contract, view, setYaw, setPitch,
    orbit: (delta) => { setYaw(s.yaw + delta); },
    tilt: (delta) => { setPitch(s.pitch + delta); },
    resetToProfile: () => { s.yaw = 0; setPitch(profile.camera.pitch); },
    setViewport: (width, height) => { s.viewport = [width, height]; sync(); },
    lookAt: (target) => { s.target = [target[0], target[1], target[2]]; sync(); },
  };
}
